import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const benefits = [
  {
    icon: 'TicketIcon',
    title: 'Unlimited Green Fees',
    body: 'Play as many rounds as you like — weekdays, weekends, and public holidays — all included in your annual membership.',
    accent: true,
    wide: true,
  },
  {
    icon: 'TrophyIcon',
    title: 'Club Competitions',
    body: 'Compete in monthly medals, Stableford events, and the prestigious Braaier Cup.',
    accent: false,
    wide: false,
  },
  {
    icon: 'UserGroupIcon',
    title: 'Community & Social',
    body: 'Join a vibrant community of over 400 members. Regular social events, sundowners, and club nights.',
    accent: false,
    wide: false,
  },
  {
    icon: 'StarIcon',
    title: 'Loyalty Rewards',
    body: 'Earn points on every round and redeem them for green fees, pro shop vouchers, and exclusive member perks.',
    accent: false,
    wide: false,
  },
  {
    icon: 'ShoppingBagIcon',
    title: 'Pro Shop Discounts',
    body: 'Members enjoy exclusive discounts on equipment, apparel, and professional coaching sessions.',
    accent: false,
    wide: false,
  },
  {
    icon: 'CalendarDaysIcon',
    title: 'Priority Tee Bookings',
    body: 'Book your preferred tee times up to 7 days in advance — before visitor slots open.',
    accent: false,
    wide: false,
  },
];

const membershipTypes = [
  { type: 'Full Member', price: 'Contact Club', note: 'Unlimited play, all competitions' },
  { type: 'Junior Member', price: 'Contact Club', note: 'Under 25 — discounted rates' },
  { type: 'Social Member', price: 'Contact Club', note: 'Clubhouse access, no green fees' },
];

export default function MembershipBenefits() {
  return (
    <section id="membership" className="py-20 bg-background">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="text-accent font-semibold text-sm tracking-widest uppercase">Membership</span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 leading-tight">
              Become Part of<br />
              <span className="text-primary">139 Years of Tradition</span>
            </h2>
          </div>
          <p className="text-muted-foreground text-base max-w-md md:text-right leading-relaxed">
            Maritzburg Golf Club membership is more than a golf subscription — it&apos;s entry into South Africa&apos;s oldest playing golf community.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {/* Wide card — Unlimited Green Fees */}
          <div className="lg:col-span-2 gradient-primary rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5 pointer-events-none" />
            <div className="absolute bottom-4 right-12 w-24 h-24 rounded-full bg-accent/10 pointer-events-none" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-accent/20 border border-accent/30 flex items-center justify-center mb-5">
                <Icon name="TicketIcon" size={24} variant="outline" className="text-accent" />
              </div>
              <h3 className="text-white font-bold text-xl mb-3">Unlimited Green Fees</h3>
              <p className="text-white/70 text-base leading-relaxed max-w-md">
                Play as many rounds as you like — weekdays, weekends, and public holidays — all included in your annual membership. No per-round charges, ever.
              </p>
              <div className="mt-6 flex items-center gap-2 text-accent text-sm font-semibold">
                <Icon name="CheckCircleIcon" size={16} variant="outline" />
                <span>18 holes · 9 holes · Practice facilities</span>
              </div>
            </div>
          </div>

          {/* Competitions */}
          <div className="bg-card border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Icon name="TrophyIcon" size={22} variant="outline" className="text-primary" />
            </div>
            <h3 className="font-bold text-foreground text-base mb-2">Club Competitions</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Compete in monthly medals, Stableford events, and the prestigious Braaier Cup. Handicap-tracked and officially registered.
            </p>
          </div>

          {/* Loyalty */}
          <div className="bg-card border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
              <Icon name="StarIcon" size={22} variant="outline" className="text-accent" />
            </div>
            <h3 className="font-bold text-foreground text-base mb-2">Loyalty Rewards</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Earn points on every round and redeem them for green fees, pro shop vouchers, and exclusive member perks.
            </p>
          </div>

          {/* Priority Bookings */}
          <div className="bg-card border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Icon name="CalendarDaysIcon" size={22} variant="outline" className="text-primary" />
            </div>
            <h3 className="font-bold text-foreground text-base mb-2">Priority Tee Bookings</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Book your preferred tee times up to 7 days in advance — before visitor slots open.
            </p>
          </div>

          {/* Community — wide */}
          <div className="md:col-span-2 lg:col-span-1 bg-muted border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Icon name="UserGroupIcon" size={22} variant="outline" className="text-primary" />
            </div>
            <h3 className="font-bold text-foreground text-base mb-2">Community & Social Events</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Join a vibrant community of over 400 members. Regular social events, sundowners, and club nights throughout the year.
            </p>
          </div>
        </div>

        {/* Membership types strip */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-muted">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Membership Categories</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
            {membershipTypes?.map((m) => (
              <div key={m?.type} className="px-6 py-5 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-foreground text-sm">{m?.type}</div>
                  <div className="text-muted-foreground text-xs mt-0.5">{m?.note}</div>
                </div>
                <div className="text-primary font-bold text-sm shrink-0">{m?.price}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            href="mailto:golf@maritzburggolfclub.co.za?subject=Membership Enquiry"
            className="btn-gold px-8 py-3.5 rounded-xl text-sm font-bold inline-flex items-center gap-2"
          >
            <Icon name="EnvelopeIcon" size={18} variant="outline" />
            Enquire About Membership
          </Link>
          <p className="text-muted-foreground text-xs mt-3">
            Contact us at <a href="tel:0333962356" className="text-primary hover:underline">033 396 2356</a> or email <a href="mailto:golf@maritzburggolfclub.co.za" className="text-primary hover:underline">golf@maritzburggolfclub.co.za</a>
          </p>
        </div>
      </div>
    </section>
  );
}
